import { useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Star, Clock, BookOpen, ArrowLeft, Zap, Globe, ShieldCheck, PlayCircle,
  Calendar, MapPin, Users, Minus, Plus, CheckCircle2, ArrowRight, Trash2, Mail, Receipt,
} from 'lucide-react'
import { gradientForCategory, formatDuration } from '../../components/ui/CourseCard'
import type { ApiCourseDetail, ApiCourseBatch } from '../../types/course'
import {
  createOrder, payOrder,
  type Order, type ParticipantInput, type CouponQuote,
} from '@/services/orderService'
import { useAuthStore } from '@/store/authStore'
import { getProfile } from '@/services/profileService'
import { type InvoiceInfo, emptyInvoiceInfo } from '@/types/invoiceInfo'
import { resolveUnitPrice, resolveOriginalPrice } from '@/lib/coursePricing'
import InvoiceInfoForm from '@/components/InvoiceInfoForm'
import RecipientEmailInput from '@/components/RecipientEmailInput'
import PaymentMethodPicker from '@/components/PaymentMethodPicker'
import { emptyRow, type EmailRow, type RowStatus } from '@/lib/recipientRow'
import CouponBox from '@/components/ui/CouponBox'
import { bangkokNowMs, registrationDeadlinePassed } from '@/lib/batchAvailability'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'
import { filterPhone, isValidPhone, isValidTaxId } from '@/lib/inputFilters'

const formatBatchDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })

// รุ่นอบรมสมัครไม่ได้เพราะอะไร — เงื่อนไขเดียวกับ ensure_batch_bookable ฝั่ง backend (ใช้แจ้งผู้ใช้ตั้งแต่เข้าหน้า ไม่ต้องรอกดชำระเงิน)
// backend ยังเป็นผู้ตัดสินจริงตอนชำระเงิน · เวลาเทียบแบบเวลาไทย (ดู lib/batchAvailability)
const batchClosedReason = (batch: ApiCourseBatch | null): string => {
  if (!batch) return ''
  const now = bangkokNowMs()
  if (batch.status !== 'OPEN') return 'รุ่นอบรมนี้ไม่ได้เปิดรับสมัครอยู่'
  if (new Date(batch.end_date ?? batch.start_date).getTime() < now) return 'รุ่นอบรมนี้ผ่านไปแล้ว'
  if (batch.registration_deadline && registrationDeadlinePassed(batch, now)) {
    return `รุ่นอบรมนี้ปิดรับสมัครแล้ว (ปิดรับเมื่อ ${formatBatchDate(batch.registration_deadline)})`
  }
  if (batch.max_seats !== -1 && batch.max_seats - batch.registered_count <= 0) return 'ที่นั่งในรุ่นอบรมนี้เต็มแล้ว'
  return ''
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const MAX_SEATS_UNLIMITED = 50   // เพดานเฉพาะรุ่น Onsite ที่ไม่จำกัดที่นั่ง (ตรงกับฝั่ง backend) — คอร์สออนไลน์ไม่จำกัดจำนวนพนักงาน

const inputClass =
  'w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted/70 outline-none transition focus:border-indigo-400'


// ── Course Thumbnail ─────────────────────────────────────────────────────────
function CourseThumbnail({ course }: { course: ApiCourseDetail }) {
  const gradient = gradientForCategory(course.category_id)
  return (
    <div className={`relative h-32 w-full overflow-hidden rounded-sm bg-gradient-to-br sm:h-full ${gradient}`}>
      {course.thumbnail_url ? (
        <img src={course.thumbnail_url} alt={course.title} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center">
          <PlayCircle className="h-10 w-10 text-text-muted" />
        </div>
      )}
    </div>
  )
}

// ── Payment Method ────────────────────────────────────────────────────────────
function PaymentMethod() {
  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-5 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">
        วิธีการชำระเงิน
      </h2>
      <PaymentMethodPicker />
    </div>
  )
}

// ── สรุปรุ่นอบรม (Onsite) ────────────────────────────────────────────────────────
function BatchInfo({ batch }: { batch: ApiCourseBatch }) {
  return (
    <div className="rounded-sm border border-border bg-surface-2 p-4">
      <p className="mb-2 text-xs font-semibold text-accent">รุ่นที่ {batch.batch_code.split('-B').pop()}</p>
      <p className="mb-1 flex items-center gap-1.5 text-sm text-text-muted">
        <Calendar className="h-3.5 w-3.5 text-text-muted" />
        {formatBatchDate(batch.start_date)}{batch.end_date ? ` - ${formatBatchDate(batch.end_date)}` : ''}
      </p>
      {batch.venue_name && (
        <p className="flex items-center gap-1.5 text-sm text-text-muted">
          <MapPin className="h-3.5 w-3.5 text-text-muted" />
          {batch.venue_name}
        </p>
      )}
      {/* วันปิดรับสมัคร (ไม่ใช่วันอบรม) — แยกให้เห็นชัด รุ่นที่ปิดรับแล้วขึ้นเป็นสีแดง */}
      {batch.registration_deadline && (
        <p className={`mt-1 flex items-center gap-1.5 text-sm ${batchClosedReason(batch) ? 'text-rose-500' : 'text-text-muted'}`}>
          <Clock className="h-3.5 w-3.5" />
          ปิดรับสมัคร {formatBatchDate(batch.registration_deadline)}
        </p>
      )}
    </div>
  )
}

// ── บุคคลทั่วไป / คอร์สฟรี: Onsite ให้ตัวเอง 1 ที่นั่ง — ไม่มีฟอร์มรายชื่อ (ใช้ชื่อจากบัญชี) ─────────
function SelfBookingCard({ batch }: { batch: ApiCourseBatch | null }) {
  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-5 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">รายละเอียดการจอง</h2>
      {batch && <div className="mb-4"><BatchInfo batch={batch} /></div>}
      <p className="flex items-center gap-2 text-sm text-text-muted">
        <Users className="h-4 w-4 shrink-0" />
        1 ที่นั่ง ในนามบัญชีของคุณ (ใช้ชื่อและอีเมลจากบัญชีที่ลงทะเบียนไว้)
      </p>
    </div>
  )
}

interface AttendeeInfo {
  first_name: string
  last_name: string
  email: string
  phone: string
  emailStatus: RowStatus   // ผลค้นหาบัญชีจากอีเมลที่กรอก (ไม่พบบัญชีก็จองได้)
  emailName?: string
}

const emptyAttendee = (): AttendeeInfo => ({ first_name: '', last_name: '', email: '', phone: '', emailStatus: 'idle' })

// ช่องที่ต้องกรอกมีเครื่องหมาย * สีแดงต่อท้ายชื่อช่อง
function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block text-xs font-medium text-text-muted">
      {children} <span className="text-rose-500">*</span>
    </label>
  )
}

// ── บริษัทจอง Onsite หลายที่นั่ง: ฟอร์มรายชื่อผู้เข้าอบรม (ไม่ต้องมีบัญชี CoWork) ─────────────────
function CompanyOnsiteForm({
  courseId, batchId, canLookup, batch, seatCount, onSeatCountChange, maxSeats, attendees, onAttendeeChange, onAttendeeEmailChange, onRemoveAttendee,
}: {
  courseId: number
  batchId: number | null
  canLookup: boolean   // ตรวจว่าอีเมลที่พิมพ์มีบัญชี CoWork หรือไม่ — ฝั่ง API อนุญาตเฉพาะบัญชีบริษัท (บุคคลทั่วไปเป็นช่องพิมพ์ธรรมดา)
  batch: ApiCourseBatch | null
  seatCount: number
  onSeatCountChange: (n: number) => void
  maxSeats: number
  attendees: AttendeeInfo[]
  onAttendeeChange: (index: number, field: 'first_name' | 'last_name' | 'phone', value: string) => void
  onAttendeeEmailChange: (index: number, next: EmailRow) => void
  onRemoveAttendee: (index: number) => void
}) {
  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-5 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">
        รายละเอียดการจอง
      </h2>

      {batch && <div className="mb-5"><BatchInfo batch={batch} /></div>}

      {/* จำนวนที่นั่ง */}
      <div className="mb-5">
        <label className="mb-2 block text-sm font-medium text-text-muted">จำนวนที่นั่งที่ต้องการจอง</label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onSeatCountChange(Math.max(1, seatCount - 1))}
            className="flex h-9 w-9 items-center justify-center rounded-sm border border-border text-text transition hover:border-accent/40 disabled:cursor-not-allowed disabled:opacity-30"
            disabled={seatCount <= 1}
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="flex w-12 items-center justify-center gap-1.5 text-base font-semibold text-text">
            <Users className="h-4 w-4 text-text-muted" /> {seatCount}
          </span>
          <button
            type="button"
            onClick={() => onSeatCountChange(Math.min(maxSeats, seatCount + 1))}
            className="flex h-9 w-9 items-center justify-center rounded-sm border border-border text-text transition hover:border-accent/40 disabled:cursor-not-allowed disabled:opacity-30"
            disabled={seatCount >= maxSeats}
          >
            <Plus className="h-4 w-4" />
          </button>
          <span className="text-xs text-text-muted">
            {batch && batch.max_seats !== -1 ? `เหลือ ${maxSeats} ที่นั่ง` : `จองได้สูงสุด ${maxSeats} ที่นั่งต่อครั้ง`}
          </span>
        </div>
      </div>

      {/* รายชื่อผู้เข้าอบรม — ชื่อ/นามสกุล/อีเมล/เบอร์โทร บังคับทุกช่อง อีเมลไม่ต้องสมัครสมาชิก CoWork */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-text-muted">รายชื่อผู้เข้าอบรม ({seatCount} ที่นั่ง)</label>
        {attendees.map((attendee, i) => (
          <div key={i} className="rounded-sm border border-border bg-surface-2 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-medium text-text-muted">ผู้เข้าอบรมคนที่ {i + 1}</p>
              <button
                type="button"
                onClick={() => onRemoveAttendee(i)}
                disabled={attendees.length <= 1}
                title={attendees.length <= 1 ? 'ต้องมีผู้เข้าอบรมอย่างน้อย 1 คน' : 'ลบผู้เข้าอบรมคนนี้ (ลดจำนวนที่นั่งลง 1)'}
                aria-label={`ลบผู้เข้าอบรมคนที่ ${i + 1}`}
                className="flex h-8 w-8 items-center justify-center rounded-sm border border-border text-text-muted transition hover:border-rose-300 hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-text-muted"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <RequiredLabel>ชื่อ</RequiredLabel>
                  <input
                    type="text"
                    value={attendee.first_name}
                    onChange={(e) => onAttendeeChange(i, 'first_name', e.target.value)}
                    placeholder="เช่น สมชาย"
                    className={inputClass}
                  />
                </div>
                <div>
                  <RequiredLabel>นามสกุล</RequiredLabel>
                  <input
                    type="text"
                    value={attendee.last_name}
                    onChange={(e) => onAttendeeChange(i, 'last_name', e.target.value)}
                    placeholder="เช่น ใจดี"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <RequiredLabel>อีเมล</RequiredLabel>
                  <RecipientEmailInput
                    value={{ email: attendee.email, status: attendee.emailStatus, name: attendee.emailName }}
                    onChange={(next) => onAttendeeEmailChange(i, next)}
                    courseId={courseId}
                    batchId={batchId}
                    takenEmails={[]}   // Onsite: อีเมลซ้ำได้ ไม่กันซ้ำ
                    requireAccount={false}
                    lookup={canLookup}
                    placeholder="name@email.com"
                  />
                </div>
                <div>
                  <RequiredLabel>เบอร์โทรศัพท์</RequiredLabel>
                  <input
                    type="tel"
                    value={attendee.phone}
                    onChange={(e) => onAttendeeChange(i, 'phone', filterPhone(e.target.value))}
                    maxLength={10}
                    placeholder="08X-XXX-XXXX"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        <p className="text-xs text-text-muted">
          <span className="text-rose-500">*</span> หมายถึงต้องกรอก · อีเมลใช้แจ้งว่าสมัครให้แล้ว ไม่จำเป็นต้องสมัครสมาชิก CoWork
          หากกรอกอีเมลที่มีบัญชีอยู่แล้ว คอร์สจะเข้าไปอยู่ในบัญชีของเขาโดยอัตโนมัติ
        </p>
      </div>
    </div>
  )
}

// ── บริษัทซื้อคอร์สออนไลน์ให้พนักงาน: พิมพ์อีเมลแล้วค้นหาบัญชีที่สมัคร CoWork ไว้ → เลือกจาก dropdown ──────
// คอร์สออนไลน์ไม่จำกัดจำนวนพนักงาน (เพดานมีเฉพาะ Onsite ตามที่นั่งที่เปิดรับ)
function EmployeeEmailRow({
  index, row, courseId, takenEmails, canRemove, onChange, onRemove,
}: {
  index: number
  row: EmailRow
  courseId: number
  takenEmails: string[]
  canRemove: boolean
  onChange: (next: EmailRow) => void
  onRemove: () => void
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-surface-2 text-xs font-semibold text-text-muted">{index + 1}</span>
      <div className="flex-1">
        <RecipientEmailInput
          value={row}
          onChange={onChange}
          courseId={courseId}
          takenEmails={takenEmails}
          requireAccount
          lookup
          withIcon
          placeholder="พิมพ์อีเมลพนักงาน เช่น name@email.com"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        className="mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border text-text-muted transition hover:border-rose-300 hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-30"
        aria-label="ลบอีเมล"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

function EmployeeEmailsForm({
  courseId, rows, onChange,
}: {
  courseId: number
  rows: EmailRow[]
  onChange: (next: EmailRow[]) => void
}) {
  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-2 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">ซื้อคอร์สให้พนักงาน</h2>
      <p className="mb-5 text-sm text-text-muted">
        พิมพ์อีเมลที่พนักงานใช้สมัครสมาชิก CoWork ไว้แล้ว ระบบจะตรวจสอบอีเมลให้ เมื่อชำระเงินสำเร็จ
        คอร์สจะเข้าไปอยู่ในบัญชีของแต่ละคนโดยอัตโนมัติ
      </p>

      <RequiredLabel>อีเมลพนักงาน</RequiredLabel>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <EmployeeEmailRow
            key={i}
            index={i}
            row={row}
            courseId={courseId}
            takenEmails={rows.filter((r, idx) => idx !== i && r.status === 'ok').map((r) => r.email.trim().toLowerCase())}
            canRemove={rows.length > 1}
            onChange={(next) => onChange(rows.map((r, idx) => (idx === i ? next : r)))}
            onRemove={() => onChange(rows.filter((_, idx) => idx !== i))}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange([...rows, emptyRow()])}
        className="mt-4 flex items-center gap-1.5 rounded-sm border border-border px-3.5 py-2 text-sm text-text transition hover:border-accent/40"
      >
        <Plus className="h-4 w-4" /> เพิ่มพนักงาน
      </button>
    </div>
  )
}

// ── Thank You Screen ──────────────────────────────────────────────────────────
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  ONLINE: 'ชำระเงินออนไลน์',
  FREE: 'ฟรี',
}

const formatPaidAt = (value: string | null) =>
  value
    ? new Date(value).toLocaleString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-'

const formatMoney = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function SummaryItem({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-text-muted">{label}</dt>
      <dd className={`mt-1 ${strong ? 'text-base font-bold text-text' : 'text-sm font-medium text-text'}`}>{value}</dd>
    </div>
  )
}

function ThankYouScreen({
  course, isOnsite, selectedBatch, order, isMulti, onContinue, continueLabel,
}: {
  course: ApiCourseDetail
  isOnsite: boolean
  selectedBatch: ApiCourseBatch | null
  order: Order | null
  isMulti: boolean   // ซื้อให้คนอื่น (บริษัท หรือ บุคคลทั่วไปซื้อ onsite ให้คนอื่น)
  onContinue: () => void
  continueLabel: string
}) {
  const participants = order?.items?.[0]?.participants ?? []
  const seats = order?.items?.[0]?.quantity ?? 1
  const isFree = (order?.final_amount ?? 0) === 0
  const typeLabel = isOnsite ? 'Onsite Training' : 'Online Course'
  // ขอใบกำกับภาษีตอนชำระเงินหรือไม่ — ขอ = มีปุ่ม "ดูใบกำกับภาษี" / ไม่ขอ = ไม่มีปุ่ม
  const hasTaxInvoice = !!order?.billing_info

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#FAFAFF] pb-20 pt-24">
      {/* พื้นหลัง logistics แบบจางๆ — ใช้ภาพเดียวกับหน้าสมัครงาน (JobApplication) ให้ธีมเว็บสม่ำเสมอกัน */}
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      <div className="relative mx-auto w-full max-w-xl px-4">
        {/* หัว — ไอคอนติ๊กถูกซ้อนวงแหวน + ข้อความยืนยัน */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-accent/10">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-accent to-accent2 shadow-lg shadow-accent/30">
              <CheckCircle2 className="h-7 w-7 text-white" strokeWidth={2.2} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-text">
            {isOnsite ? 'จองอบรมสำเร็จ' : isFree ? 'ลงทะเบียนสำเร็จ' : 'ชำระเงินสำเร็จ'}
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-text-muted">
            {isOnsite
              ? 'การจองอบรมของคุณสำเร็จแล้ว ทีมงานจะติดต่อกลับเพื่อยืนยันรายละเอียดการอบรมเพิ่มเติม'
              : isMulti && !isFree
                ? 'คอร์สถูกเพิ่มให้พนักงานตามอีเมลที่ระบุเรียบร้อยแล้ว'
                : 'ลงทะเบียนคอร์สเรียบร้อยแล้ว คุณสามารถเริ่มเรียนได้เลย'}
          </p>
        </div>

        {/* ข้อมูลคอร์ส / ผู้เข้าอบรม / สรุปคำสั่งซื้อ — วางบนพื้นหลังโดยตรง ไม่มีกรอบการ์ดสีขาว คั่นด้วยเส้นบาง */}
        <div className="px-2 sm:px-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <span className="rounded-full bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-accent">
              {typeLabel}
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" /> ยืนยันแล้ว
            </span>
          </div>

          <h2 className="mb-4 text-xl font-bold leading-snug text-text">{course.title}</h2>

          <div className="flex flex-wrap gap-2 text-sm text-text-muted">
            {isOnsite && selectedBatch && (
              <span className="flex items-center gap-1.5 rounded-lg bg-[#F6F5FF] px-3 py-1.5">
                <Calendar className="h-3.5 w-3.5 text-accent" />
                {formatBatchDate(selectedBatch.start_date)}
                {selectedBatch.end_date ? ` - ${formatBatchDate(selectedBatch.end_date)}` : ''}
              </span>
            )}
            {isOnsite && selectedBatch?.venue_name && (
              <span className="flex items-center gap-1.5 rounded-lg bg-[#F6F5FF] px-3 py-1.5">
                <MapPin className="h-3.5 w-3.5 text-accent" />
                {selectedBatch.venue_name}
              </span>
            )}
            <span className="flex items-center gap-1.5 rounded-lg bg-[#F6F5FF] px-3 py-1.5">
              <Users className="h-3.5 w-3.5 text-accent" />
              {seats} {isOnsite ? 'ที่นั่ง' : 'คน'}
            </span>
          </div>

          {isMulti && participants.length > 0 && (
            <>
              <div className="my-6 border-t border-[#DCD9EE]" />
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
                {isOnsite ? 'รายชื่อผู้เข้าอบรม' : 'พนักงานที่ได้รับคอร์ส'}
              </p>
              <ul className="space-y-2.5">
                {participants.map((p) => (
                  <li key={p.email} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-text">{p.first_name ? `${p.first_name} ${p.last_name ?? ''}`.trim() : p.email}</span>
                    {!p.has_account && (
                      <span className="shrink-0 rounded-full bg-[#F1F0F7] px-2.5 py-0.5 text-[11px] text-text-muted">
                        ยังไม่ได้เชื่อมบัญชี
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {order && (
            <>
              <div className="my-6 border-t border-[#DCD9EE]" />
              <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
                <SummaryItem label="หมายเลขคำสั่งซื้อ" value={order.order_number} />
                <SummaryItem label="วันที่" value={formatPaidAt(order.paid_at)} />
                {!isFree && (
                  <>
                    <SummaryItem
                      label="วิธีชำระเงิน"
                      value={PAYMENT_METHOD_LABELS[order.payment?.payment_method ?? ''] ?? '-'}
                    />
                    <SummaryItem label="ยอดชำระ" value={`฿${formatMoney(order.final_amount)}`} strong />
                  </>
                )}
              </dl>
            </>
          )}
        </div>

        {/* ใบยืนยันถูกส่งไปทางอีเมล (ไม่ส่งเมื่อยอด 0 บาท) */}
        {order && !isFree && (
          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-text-muted">
            <Mail className="h-3.5 w-3.5" />
            ใบยืนยันการสั่งซื้อจะถูกส่งไปที่อีเมลของคุณ
          </p>
        )}

        {/* ปุ่มหลัก + ปุ่มรอง */}
        <div className="mt-7 flex flex-col items-center gap-4">
          <button
            onClick={onContinue}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent to-accent2 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40 sm:w-auto sm:min-w-[260px]"
          >
            {continueLabel}
            <ArrowRight className="h-4 w-4" />
          </button>

          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            {order && !isFree && hasTaxInvoice && (
              <Link
                to={`/receipt/${order.order_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#DDD9F7] bg-white px-4 py-2 font-medium text-accent transition-colors hover:bg-[#F6F5FF]"
              >
                <Receipt className="h-4 w-4" />
                ดูใบกำกับภาษี
              </Link>
            )}
            <Link
              to={`/courses/${course.slug}`}
              className="rounded-lg px-4 py-2 text-text-muted transition-colors hover:text-accent"
            >
              กลับไปหน้าคอร์ส
            </Link>
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function EnrollCoursePage() {
  const { state } = useLocation() as { state: { course?: ApiCourseDetail; batchId?: number | null } | null }
  const navigate = useNavigate()
  const customerType = useAuthStore((s) => s.customerType)
  const course = state?.course
  const [paidOrder, setPaidOrder] = useState<Order | null>(null)
  const [coupon, setCoupon] = useState<CouponQuote | null>(null)   // คูปองที่ตรวจผ่านแล้ว (ส่วนลดคำนวณที่ backend)
  const [seatCount, setSeatCount] = useState(1)
  const [attendees, setAttendees] = useState<AttendeeInfo[]>([emptyAttendee()])
  const [emailRows, setEmailRows] = useState<EmailRow[]>([emptyRow()])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo>(() =>
    emptyInvoiceInfo(customerType === 'COMPANY' ? 'COMPANY' : 'INDIVIDUAL', false)
  )

  const isCompany = customerType === 'COMPANY'

  // บัญชีบริษัท: ดึงเฉพาะชื่อบริษัท/เลขผู้เสียภาษีจากโปรไฟล์มาเติมให้อัตโนมัติ — ที่ตั้งบริษัท (ที่อยู่/จังหวัด/เขต-อำเภอ/
  // แขวง-ตำบล/รหัสไปรษณีย์/รหัสสาขา) บังคับให้กรอกเองทุกครั้งเสมอ ไม่ auto-fill (อาจต้องออกเอกสารไปที่อยู่คนละที่กับโปรไฟล์)
  useEffect(() => {
    if (!isCompany) return
    getProfile()
      .then((p) => {
        if (!('company_name' in p)) return
        setInvoiceInfo((prev) => ({
          ...prev,
          companyName: prev.companyName || p.company_name || '',
          taxId: prev.taxId || p.tax_id || '',
        }))
      })
      .catch(() => {})
  }, [isCompany])

  const isOnsite = course?.course_format === 'ONSITE'
  const isFree = !!course?.is_free
  // คอร์สเสียเงิน = ซื้อให้คนอื่นได้ (Onsite: หลายที่นั่ง+รายชื่อ — บริษัทหรือบุคคลทั่วไปก็ได้ / ออนไลน์: เฉพาะบริษัทซื้อให้พนักงานด้วยอีเมล)
  const groupOnsite = !isFree && !!isOnsite
  const companyGift = isCompany && !isFree && !isOnsite
  const isMulti = groupOnsite || companyGift

  // เปลี่ยนจำนวนที่นั่ง → ปรับแถวรายชื่อผู้เข้าอบรมให้ตรง (คงข้อมูลที่กรอกไว้) — ไม่มี Order ค้างให้แก้ไข
  // (ไม่มีการกันที่นั่งล่วงหน้าแล้ว) ที่นั่งจะถูกเช็คสดจริงตอนกดจ่ายเงินเท่านั้น
  const resizeAttendees = (n: number) =>
    setAttendees((prev) => Array.from({ length: n }, (_, i) => prev[i] || emptyAttendee()))
  const handleSeatCountChange = (n: number) => {
    setSeatCount(n)
    resizeAttendees(n)
  }

  // ลบผู้เข้าอบรมรายคน: ลดจำนวนที่นั่งลง 1
  const handleRemoveAttendee = (index: number) => {
    if (attendees.length <= 1) return
    const next = attendees.filter((_, i) => i !== index)
    setAttendees(next)
    setSeatCount(next.length)
  }

  const handleAttendeeChange = (index: number, field: 'first_name' | 'last_name' | 'phone', value: string) => {
    setAttendees((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)))
  }
  const handleAttendeeEmailChange = (index: number, next: EmailRow) => {
    setAttendees((prev) =>
      prev.map((a, i) => (i === index ? { ...a, email: next.email, emailStatus: next.status, emailName: next.name } : a))
    )
  }

  const selectedBatch = useMemo(
    () => (isOnsite ? course?.batches.find((b) => b.batch_id === state?.batchId) || null : null),
    [course, isOnsite, state?.batchId]
  )

  // ที่นั่งที่จองได้: ที่นั่งเหลือจริง ณ ตอนนี้ (ไม่มีการกันที่นั่งล่วงหน้าแล้ว) — รุ่นไม่จำกัดที่นั่งใช้เพดาน MAX_SEATS_UNLIMITED
  const maxSeats = selectedBatch && selectedBatch.max_seats !== -1
    ? Math.max(1, selectedBatch.max_seats - selectedBatch.registered_count)
    : MAX_SEATS_UNLIMITED

  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFB] pt-24">
        <div className="text-center">
          <p className="mb-4 text-text-muted">ไม่พบข้อมูลคอร์ส</p>
          <Link to="/courses" className="text-sm text-orange-400 hover:underline">กลับหน้าคอร์สทั้งหมด</Link>
        </div>
      </div>
    )
  }

  // บัญชีบริษัทเป็นผู้ซื้อให้ทีม ไม่ใช่ผู้เรียน — คอร์สฟรีสมัครไม่ได้
  if (isCompany && isFree) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFB] pt-24">
        <div className="max-w-md px-4 text-center">
          <p className="mb-2 text-lg font-semibold text-text">บัญชีบริษัทไม่สามารถลงทะเบียนเรียนเองได้</p>
          <p className="mb-5 text-sm text-text-muted">บริษัทซื้อคอร์สให้พนักงานได้เฉพาะคอร์สเสียเงิน หากต้องการเรียนคอร์สฟรี กรุณาใช้บัญชีบุคคลทั่วไป</p>
          <Link to={`/courses/${course.slug}`} className="text-sm text-accent hover:underline">กลับไปหน้าคอร์ส</Link>
        </div>
      </div>
    )
  }

  // แถวอีเมลที่ยังว่างไม่นับ — จำนวนคนที่ซื้อให้ = แถวที่กรอกแล้ว (คอร์สออนไลน์ไม่จำกัดจำนวน)
  const filledRows = emailRows.filter((r) => r.email.trim())
  const giftEmails = filledRows.map((r) => r.email.trim().toLowerCase())
  const seats = companyGift ? giftEmails.length : groupOnsite ? seatCount : 1

  // Onsite: สมัครซ้ำ/ชื่อซ้ำ/อีเมลซ้ำได้ไม่จำกัด (ไม่กันซ้ำ) แต่ต้องกรอกอีเมลผู้เข้าอบรมทุกคน (ไม่ต้องมีบัญชี CoWork — ใช้ส่งอีเมลแจ้งว่าสมัครให้แล้ว)
  const attendeesValid =
    attendees.every(
      (a) =>
        a.first_name.trim() &&
        a.last_name.trim() &&
        isValidPhone(a.phone.trim()) &&
        EMAIL_RE.test(a.email.trim())   // อีเมลบังคับและต้องถูกรูปแบบ (ซ้ำได้)
    )
  // ทุกอีเมลที่กรอกต้องเป็นบัญชีที่ระบบตรวจพบและใช้ได้ (status ok) และไม่ซ้ำกัน
  const emailsValid =
    filledRows.length >= 1 && filledRows.every((r) => r.status === 'ok') && new Set(giftEmails).size === giftEmails.length

  // ต้องกรอกข้อมูลผู้สั่งซื้อครบเมื่อ: เป็นบัญชีบริษัท (บังคับเสมอ), เลือกประเภทนิติบุคคล/บริษัท, หรือเลือก "ต้องการใบกำกับภาษี" ในฐานะบุคคลทั่วไป
  const needsInvoiceInfo = isCompany || invoiceInfo.recipientType === 'COMPANY' || invoiceInfo.wantsInvoice
  const invoiceInfoValid = !needsInvoiceInfo || (
    invoiceInfo.recipientType === 'COMPANY'
      ? !!(invoiceInfo.companyName.trim() && isValidTaxId(invoiceInfo.taxId) && invoiceInfo.address.trim() &&
           invoiceInfo.province && invoiceInfo.district.trim() && invoiceInfo.subdistrict.trim() && invoiceInfo.postalCode.trim())
      : !!(invoiceInfo.fullName.trim() && isValidTaxId(invoiceInfo.taxId) && invoiceInfo.address.trim() &&
           invoiceInfo.province && invoiceInfo.district.trim() && invoiceInfo.subdistrict.trim() && invoiceInfo.postalCode.trim())
  )

  // รุ่นอบรมที่เลือกสมัครไม่ได้ (ปิดรับ/ผ่านไปแล้ว/เต็ม) — แจ้งเหนือปุ่มชำระเงินและปิดปุ่ม (ไม่เกี่ยวกับคูปอง)
  const batchBlock = isOnsite ? batchClosedReason(selectedBatch) : ''
  const canPay = (groupOnsite ? attendeesValid : companyGift ? emailsValid : true) && invoiceInfoValid && !batchBlock
  const blockReason = batchBlock
    ? `${batchBlock} กรุณาย้อนกลับไปเลือกรุ่นอื่น หรือติดต่อทีมงาน`
    : groupOnsite && !attendeesValid
    ? 'กรุณากรอกชื่อ นามสกุล อีเมล และเบอร์โทรศัพท์ ของผู้เข้าอบรมให้ครบทุกที่นั่ง (อีเมลต้องถูกต้อง)'
    : companyGift && !emailsValid
      ? filledRows.length === 0
        ? 'กรุณาพิมพ์อีเมลพนักงาน'
        : 'ทุกอีเมลต้องเป็นบัญชี CoWork ที่มีอยู่ในระบบ และไม่ซ้ำกัน'
      : !invoiceInfoValid
        ? 'กรุณากรอกข้อมูลผู้สั่งซื้อสำหรับใบกำกับภาษีให้ครบ'
        : ''

  const handleContinue = () => {
    if (groupOnsite || companyGift) return navigate('/account?tab=orders')
    if (isOnsite) return navigate('/account?tab=courses')
    return navigate('/course-room', { state: { course } })
  }
  const continueLabel = groupOnsite || companyGift
    ? 'ดูประวัติคำสั่งซื้อ'
    : isOnsite ? 'ดูคอร์สของฉัน' : 'เข้าสู่ห้องเรียน'

  // ทุกคอร์ส (รวมฟรี รวม Onsite) เหมือนกันหมด: สร้างคำสั่งซื้อตอนกดปุ่มนี้เลย แล้วจ่ายต่อทันที — ไม่มีการกันที่นั่งล่วงหน้า
  // ที่นั่งเช็คสดจริงตอนนี้ (ฝั่ง backend มี row lock กันสองคนจ่ายพร้อมกันแย่งที่นั่งสุดท้าย) ถ้าเต็มพอดีจะ error ทันที
  // จ่ายออนไลน์ต่อ Payment Gateway จริง (redirect ไปหน้า TEP แล้วเลือกช่องทางที่นั่น) · คอร์สฟรีใช้วิธี FREE (ยอด 0 บาท)
  // บุคคลทั่วไปและคอร์สฟรีไม่ส่งรายชื่อ (ให้ตัวเอง ระบบใช้ชื่อจากบัญชี) / บริษัทส่งรายชื่อตามฟอร์ม
  const handleSubmit = async () => {
    setSubmitError('')
    setSubmitting(true)
    try {
      const order = await createOrder('COURSE', course.course_id, seats, isOnsite ? state?.batchId : null, coupon?.code)
      const participants: ParticipantInput[] | undefined = groupOnsite
        ? attendees.map((a) => ({
            email: a.email.trim().toLowerCase(),
            first_name: a.first_name.trim(),
            last_name: a.last_name.trim(),
            phone: a.phone.trim(),
          }))
        : companyGift
          ? giftEmails.map((email) => ({ email }))
          : undefined
      // ยอดเป็น 0 (คอร์สฟรี หรือคูปองลดเต็มจำนวน) ใช้วิธีชำระ FREE ตามกติกาของ backend
      const method = isFree || (coupon && coupon.final_amount <= 0) ? 'FREE' : 'ONLINE'
      const result = await payOrder(order.order_id, method, participants, invoiceInfo)
      if (result.redirect_url) {
        // จ่ายออนไลน์: order ยังเป็น PENDING — พาลูกค้าไปหน้าจ่ายของ TEP แล้ว TEP พากลับมาที่ /payment/result/:orderId
        window.location.assign(result.redirect_url)
        return
      }
      setPaidOrder(result)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'ลงทะเบียนไม่สำเร็จ')
    } finally {
      setSubmitting(false)
    }
  }

  if (paidOrder) {
    return (
      <ThankYouScreen
        course={course}
        isOnsite={!!isOnsite}
        selectedBatch={selectedBatch}
        order={paidOrder}
        isMulti={isMulti}
        onContinue={handleContinue}
        continueLabel={continueLabel}
      />
    )
  }

  // ราคาต่อคนต้องคำนวณตามจำนวนคน (seats) เสมอ — คอร์สที่ตั้งราคาขั้นบันไดไว้ ราคาต่อคนเปลี่ยนไปตามจำนวนที่เลือก ไม่ใช่ course.price ตรงๆ
  const unitPrice = resolveUnitPrice(course, seats)
  const unitOriginalPrice = resolveOriginalPrice(course, seats)
  const discountPct = unitOriginalPrice && !isFree
    ? Math.round((1 - unitPrice / unitOriginalPrice) * 100)
    : null
  const totalPrice = unitPrice * seats
  const totalOriginalPrice = unitOriginalPrice ? unitOriginalPrice * seats : null
  // คูปอง: ส่วนลดและยอดสุทธิใช้ค่าที่ backend คำนวณ (authoritative) — ไม่คำนวณเองฝั่งหน้าเว็บ
  const couponDiscount = coupon ? coupon.discount : 0
  const payableTotal = coupon ? coupon.final_amount : totalPrice
  const payZero = isFree || payableTotal <= 0

  return (
    <div className="min-h-screen bg-[#FAFAFB] pt-0">
      {/* Hero banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-accent via-accent2 to-accentBlue">
        <div className="pointer-events-none absolute inset-0 bg-black/60" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6" style={{ paddingTop: '80px', paddingBottom: '32px' }}>
          <Link
            to={`/courses/${course.slug}`}
            className="mb-3 flex w-fit items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> กลับไปหน้ารายละเอียดคอร์ส
          </Link>
          <h1 className="text-3xl font-bold text-white md:text-4xl">คำสั่งซื้อ</h1>
        </div>
      </div>

      {/* Body */}
      <div className="bg-[#FAFAFB] pb-16">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">

            {/* LEFT */}
            <div className="space-y-6 lg:col-span-3">
              {/* รายละเอียดคอร์ส */}
              <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
                <h2 className="mb-6 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">
                  รายละเอียดคอร์ส
                </h2>
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="w-full shrink-0 sm:w-52">
                    <CourseThumbnail course={course} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="rounded bg-orange-500/20 px-2 py-0.5 text-xs font-medium text-orange-400">
                      {course.course_format === 'ONSITE' ? 'Onsite Training' : 'Online Course'}
                    </span>
                    <h3 className="mt-2 text-base font-semibold leading-snug text-text">{course.title}</h3>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-text-muted">
                      <span className="flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        <span className="font-semibold text-amber-400">{course.avg_rating.toFixed(1)}</span>
                        <span>({course.review_count.toLocaleString()} รีวิว)</span>
                      </span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDuration(course.duration_minutes)}</span>
                      <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" />{course.lesson_count} บทเรียน</span>
                      <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{course.language === 'TH' ? 'ภาษาไทย' : course.language}</span>
                    </div>
                  </div>
                  <div className="hidden shrink-0 text-right sm:block">
                    {isFree ? (
                      <span className="text-xl font-bold text-emerald-400">ฟรี</span>
                    ) : (
                      <div>
                        <div className="text-xl font-bold text-text">฿{unitPrice.toLocaleString()}</div>
                        {unitOriginalPrice && <div className="text-xs text-text-muted line-through">฿{unitOriginalPrice.toLocaleString()}</div>}
                        {discountPct && <span className="mt-1 inline-block rounded bg-orange-500/20 px-1.5 py-0.5 text-xs font-bold text-orange-400">-{discountPct}%</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* รายละเอียดการจอง */}
              {groupOnsite && (
                <CompanyOnsiteForm
                  courseId={course.course_id}
                  batchId={state?.batchId ?? null}
                  canLookup={isCompany}
                  batch={selectedBatch}
                  seatCount={seatCount}
                  onSeatCountChange={handleSeatCountChange}
                  maxSeats={maxSeats}
                  attendees={attendees}
                  onAttendeeChange={handleAttendeeChange}
                  onAttendeeEmailChange={handleAttendeeEmailChange}
                  onRemoveAttendee={handleRemoveAttendee}
                />
              )}
              {companyGift && (
                <EmployeeEmailsForm courseId={course.course_id} rows={emailRows} onChange={setEmailRows} />
              )}
              {isOnsite && !groupOnsite && <SelfBookingCard batch={selectedBatch} />}

              <InvoiceInfoForm isCompany={isCompany} info={invoiceInfo} onChange={setInvoiceInfo} />

              {/* วิธีการชำระเงิน — เฉพาะคอร์สที่ไม่ฟรี */}
              {!payZero && <PaymentMethod />}
            </div>

            {/* RIGHT */}
            <div>
              <div className="sticky top-24 space-y-4">
                <div className="overflow-hidden rounded-sm border border-border bg-surface shadow-sm">
                  <div className="border-b border-border bg-indigo-500/10 px-5 py-3 font-semibold text-text">สรุปการสั่งซื้อ</div>
                  <div className="p-5">
                    {!isFree && totalOriginalPrice && (
                      <div className="mb-3 flex justify-between text-sm text-text-muted">
                        <span>ราคาปกติ</span>
                        <span className="line-through">฿{totalOriginalPrice.toLocaleString()}</span>
                      </div>
                    )}
                    {discountPct && totalOriginalPrice && (
                      <div className="mb-3 flex justify-between text-sm text-emerald-400">
                        <span>ส่วนลด -{discountPct}%</span>
                        <span className="font-medium">-฿{(totalOriginalPrice - totalPrice).toLocaleString()}</span>
                      </div>
                    )}
                    {isMulti && (() => {
                      const activeTier = (course.price_tiers ?? []).find((t) => t.min_qty <= seats && (t.max_qty == null || seats <= t.max_qty))
                      const unitLabel = groupOnsite ? 'ค่าอบรม/คน' : 'ค่าคอร์ส/คน'
                      const seatLabel = groupOnsite ? 'จำนวนที่นั่ง' : 'จำนวนคน'
                      return (
                        <>
                          <div className="mb-2 flex justify-between text-sm text-text-muted">
                            <span>{unitLabel}</span>
                            <span className="text-right text-text">
                              ฿{unitPrice.toLocaleString()}
                              {activeTier && activeTier.min_qty > 1 && (
                                <span className="block text-xs text-text-muted">(ราคาสำหรับการจองตั้งแต่ {activeTier.min_qty} คนขึ้นไป)</span>
                              )}
                            </span>
                          </div>
                          <div className="mb-3 flex justify-between text-sm text-text-muted">
                            <span>{seatLabel}</span>
                            <span className="font-medium text-text">{seats}</span>
                          </div>
                        </>
                      )
                    })()}
                    {!isFree && (
                      <div className="mb-3">
                        <CouponBox
                          itemType="COURSE" refId={course.course_id} quantity={seats}
                          batchId={isOnsite ? state?.batchId : null}
                          applied={coupon} onApplied={setCoupon} disabled={submitting}
                        />
                      </div>
                    )}
                    {coupon && (
                      <div className="mb-3 flex justify-between text-sm text-emerald-500">
                        <span>ส่วนลดคูปอง ({coupon.code})</span>
                        <span className="font-medium">-฿{couponDiscount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="my-3 border-t border-dashed border-border" />
                    <div className="mb-4 flex justify-between font-bold text-text">
                      <span>สรุปยอดชำระ</span>
                      <span className={payZero ? 'text-emerald-400' : ''}>
                        {payZero ? 'ฟรี' : `฿${payableTotal.toLocaleString()}`}
                      </span>
                    </div>
                    {!isFree && (
                      <p className="mb-5 text-xs leading-relaxed text-text-muted">
                        เมื่อชำระเงินแล้ว ถือว่าท่านยอมรับ{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">ข้อตกลงและเงื่อนไขการใช้บริการ</a>,{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">ประกาศความเป็นส่วนตัวสำหรับผู้ใช้บริการ</a>{' '}
                        และ <a href="/refund" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">นโยบายการคืนเงิน</a> เรียบร้อยแล้ว
                      </p>
                    )}
                    {blockReason && <p className="mb-3 text-xs text-amber-500">{blockReason}</p>}
                    {submitError && <p className="mb-3 text-xs text-rose-500">{submitError}</p>}
                    <button
                      onClick={handleSubmit}
                      disabled={!canPay || submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-indigo-500 py-3 font-semibold text-white transition hover:bg-violet-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                    >
                      {isFree && <Zap className="h-4 w-4" />}
                      {submitting
                        ? 'กำลังดำเนินการ...'
                        : isFree
                          ? isOnsite ? 'ยืนยันการสมัคร' : 'เริ่มเรียนฟรี'
                          : 'ชำระเงิน'}
                    </button>
                  </div>
                </div>
                {!isFree && (
                  <div className="rounded-sm border border-border bg-surface p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-medium text-text-muted">
                      <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-400" />
                      คืนเงินได้ 100% ภายใน 7 วัน
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-text-muted">
                      ไม่พอใจในคอร์ส? เราคืนเงินให้ทันทีโดยไม่มีเงื่อนไข
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
